// ─── Public Status Page — one system ─────────────────────────────────
//
// Two shapes, one row.
//
//  * A MEASURED component: name, optional description, its status pill and its
//    day-per-cell history bar.
//  * A LINK-OUT component (externalUrl set): the row carries an <a> to the other
//    system — "Åbn tjenesten", where the pill would be — and no history bar at
//    all. Such a component has no status of its own — we are pointing at
//    somebody else's page, and painting a green pill next to a service we do not
//    measure would be a lie the moment that service breaks.
//
//    Only that affordance navigates; the row around it is inert. It used to be
//    the other way round (the whole row was the anchor), which meant a link-out
//    that also has CHILDREN nested its disclosure <button> inside the <a>: the
//    click bubbled and expanding the branch opened the other system in a new
//    tab. Nesting a button in an anchor is invalid HTML for exactly this reason,
//    so the anchor shrank to the one thing that should navigate.
//
// The row is a CSS grid (see .sp-comp in status.css): one column with the bar
// underneath on a phone, three columns with the bar inline from 861px up.
//
// A row that has CHILDREN also carries the disclosure that opens them. The
// button is part of the row rather than a wrapper around it so the grid columns
// still line up across every level of the tree — see .sp-comp-toggle.

import type { PublicStatusComponent } from '@pcp/shared';
import { da } from '../i18n/da';
import { levelForStatus } from '../lib/levels';
import { isPlainLeftClick } from '../router';
import { SIcon } from '../ui/SIcon';
import { VisuallyHidden } from '../ui/VisuallyHidden';
import { HistoryGrid } from './HistoryGrid';

export interface ComponentRowProps {
  /** Day navigation, threaded to the strip. Stable callbacks — see HistoryGridProps. */
  hrefForDay?: (date: string, componentId: string | null) => string;
  onOpenDay?: (date: string, componentId: string | null) => void;
  component: PublicStatusComponent;
  historyDays: number;
  historyStartDate: string;
  /** `summary.dayMinutes`; the strip's uptime figure divides by it. */
  dayMinutes?: readonly number[];
  /** Renders the disclosure. False for a leaf, which gets a spacer instead so
   *  every name at the same level starts at the same x. */
  hasChildren?: boolean;
  expanded?: boolean;
  onToggle?: () => void;
  /** id of the element the disclosure controls (aria-controls). */
  childrenId?: string;
  /** href of this component's own page. The NAME becomes the link — never the
   *  row (see the header note) and never the whole name block, which also holds
   *  the disclosure. */
  detailHref: string;
  onOpenDetail: () => void;
}

/** The operator-supplied icon, when there is one. Rendered decoratively (the
 *  name is right beside it) and removed on a load error rather than leaving a
 *  broken-image glyph — an external icon host is exactly the kind of thing that
 *  is also down during an incident. */
function ComponentIcon({ url, name }: { url: string | null; name: string }) {
  if (!url) return null;
  return (
    <img
      className="sp-comp-icon"
      src={url}
      alt=""
      title={name}
      loading="lazy"
      decoding="async"
      onError={(event) => {
        event.currentTarget.style.display = 'none';
      }}
    />
  );
}

export function ComponentRow({
  component,
  hrefForDay,
  onOpenDay,
  historyDays,
  historyStartDate,
  dayMinutes,
  hasChildren = false,
  expanded = false,
  onToggle,
  childrenId,
  detailHref,
  onOpenDetail,
}: ComponentRowProps) {
  const toggle = hasChildren ? (
    <button
      type="button"
      className="sp-comp-toggle"
      aria-expanded={expanded}
      aria-controls={childrenId}
      onClick={onToggle}
    >
      {/* One chevron, ROTATED by CSS on [aria-expanded="true"] — a single glyph
          that turns reads as the same object moving, where a swap reads as two.
          Decorative: the accessible name below says what the button does. */}
      <SIcon name="chevronRight" size={14} />
      <VisuallyHidden>
        {expanded ? da.collapseComponent(component.name) : da.expandComponent(component.name)}
      </VisuallyHidden>
    </button>
  ) : (
    <span className="sp-comp-toggle sp-comp-toggle-empty" aria-hidden="true" />
  );

  // The NAME is the link into /component/<id> — not the row, and not the block
  // below, which also holds the disclosure. Same reasoning as the link-out fix
  // above: one interactive control, one job.
  const name = (
    <span className="sp-comp-name">
      {toggle}
      <ComponentIcon url={component.iconUrl} name={component.name} />
      <a
        className="sp-comp-name-text sp-comp-name-link"
        href={detailHref}
        onClick={(event) => {
          if (!isPlainLeftClick(event)) return;
          event.preventDefault();
          onOpenDetail();
        }}
      >
        {component.name}
        {/* The visible text is just the name, which on its own reads as a
            heading rather than as somewhere to go. */}
        <VisuallyHidden>{` — ${da.componentOpen(component.name)}`}</VisuallyHidden>
      </a>
    </span>
  );

  if (component.externalUrl) {
    return (
      <div className={`sp-comp sp-comp--d${component.depth}`}>
        <div className="sp-comp-main">
          {name}
          {component.description && <span className="sp-comp-desc">{component.description}</span>}
        </div>
        <a
          className="sp-comp-status sp-comp-link sp-comp-external"
          href={component.externalUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          {da.externalLink}
          <SIcon name="external" size={14} />
          {/* The visible label is the same on every link-out row, so the name
              of the service it opens only reaches a screen reader from here. */}
          <VisuallyHidden>{` — ${component.name}`}</VisuallyHidden>
        </a>
      </div>
    );
  }

  const level = levelForStatus(component.status);
  const hasHistory = historyDays > 0 && Boolean(component.history);

  return (
    <div className={`sp-comp sp-comp--d${component.depth}`}>
      <div className="sp-comp-main">
        {name}
        {component.description && <span className="sp-comp-desc">{component.description}</span>}
      </div>

      <div className="sp-comp-status">
        <span className={`sp-pill sp-pill--${level.modifier}`}>
          <SIcon name={level.icon} size={12} />
          {level.label}
        </span>
      </div>

      {hasHistory && (
        <div className="sp-comp-history">
          <HistoryGrid
            history={component.history}
            info={component.info}
            hrefForDay={hrefForDay}
            onOpenDay={onOpenDay}
            historyDays={historyDays}
            historyStartDate={historyStartDate}
            downtime={component.downtime}
            dayMinutes={dayMinutes}
            label={component.name}
            size="sm"
          />
        </div>
      )}
    </div>
  );
}
